export const en = {
    // Navbar
    navbar: {
      schools: 'Schools',
      howItWorks: 'How It Works',
      aboutMe: 'About Me',
      pricing: 'Pricing',
      faqs: 'FAQs',
      memories: 'Memories',
      myAccount: 'My account',
    },
    // Home Page - HERO MAIN (H1, tagline, subtitle)
    home: {
      title: 'Find your surf photos in seconds',
      tagline: 'Surf photography in Lanzarote',
      subtitle: 'Upload a photo and find your memories instantly, without searching through hundreds of images',
      cta: 'Find My Photos',
      stats: {
        photos: 'Photos',
        schools: 'Schools',
        surfers: 'Happy Surfers',
      },
    },
    // Schools Page - SURF SCHOOLS
    schools: {
      title: 'Surf Schools',
      subtitle: 'Select your surf school to find your photos',
      viewPhotos: 'View Photos',
      introTitle: 'Your school, your day, your memories',
      descriptionMain: 'Select the surf school where you took your lesson and find your session organized by date.',
      descriptionSecondary: 'Daily surf photography sessions in Famara and La Santa.',
      loading: 'Loading schools...',
      apiUnavailable:
        'We could not reach the server (timeout or error). Showing the default school list — try again in a few minutes.',
      retry: 'Retry',
      otherSchools: 'OTHER SCHOOLS',
      valueHighQuality: 'High quality photos',
      valueInstantDownload: 'Instant download',
      valueSecurePayment: 'Secure payment',
    },
    // How It Works Page - PHOTO ACCESS (SELFIE)
    howItWorks: {
      title: 'Find your photos securely',
      subtitle: 'Upload a selfie so our system can automatically identify only your photos.',
      step1: {
        title: 'Choose Your Surf School',
        description: 'Select the school where you took your class',
      },
      step2: {
        title: 'Select Your Session Date',
        description: 'Choose the day you surfed',
      },
      step3: {
        title: 'Upload a Selfie',
        description: 'Our system will automatically identify only your photos',
      },
      step4: {
        title: 'View Your Photos',
        description: 'See all the photos you appear in',
      },
      step5: {
        title: 'Add Your Memories',
        description: 'Select the photos you want to keep',
      },
      step6: {
        title: 'Pay Securely',
        description: 'Make payment quickly and securely',
      },
      step7: {
        title: 'Instant Download',
        description: 'Get your photos instantly without watermark',
      },
      smartSearch: {
        title: 'Smart Search',
        description: 'This process guarantees your privacy and prevents other people from accessing or purchasing photos that do not belong to them.',
      },
      cta: 'Find Your Photos',
    },
    // About Me Page - ABOUT ME
    aboutMe: {
      label: 'About Me',
      title: 'Capturing your best',
      titleHighlight: 'moments in the water',
      intro: 'I\'m <strong>Germán Abreu</strong>, a photographer specialized in surf and water sports in Lanzarote. I live in La Santa and work mainly in Famara and other surf spots around the island.',
      paragraph1: 'After years photographing surfers and surf school students, I always detected the same problem: finding your photos among hundreds of images was slow and impractical.',
      paragraph2: 'That\'s how <strong className="brand-name">Surf Photo Lanzarote</strong> was born, a simple, fast, and secure way for each person to find only their photos, without endless searching and always respecting privacy.',
      mission: 'My goal is for you to take home an authentic memory of your experience in the water. Because waves pass… but memories remain.',
      stats: {
        experience: '+8 years',
        experienceLabel: 'Experience',
        schools: '+7',
        schoolsLabel: 'Partner schools',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Pricing',
      subtitle: 'Choose the option that best suits you',
      plan1: {
        title: 'Your best memory',
        description: 'One individual high-quality image',
        details: [
          'Instant download',
          'High resolution',
          'JPG format',
          'Suitable for printing',
          'No watermark',
        ],
      },
      plan2: {
        title: 'All the memories from your session',
        description: 'All photos of the same person on the same day.',
        details: [
          'All photos from the day',
          'Instant download',
          'High resolution',
          'JPG format',
          'No watermark',
        ],
      },
      plan3: {
        title: 'Additional Day',
        description: 'Add another day to your pack.',
        details: [
          'All photos from the extra day',
          'Instant download',
          'High resolution',
          'No watermark',
        ],
      },
      plan4: {
        title: 'Additional Person',
        description: 'Add the photos of another person. It can be from the same day or from a different day.',
        details: [
          'All photos of another person',
          'Instant download',
          'High resolution',
          'No watermark',
        ],
      },
      includes: 'Includes',
      note: 'All prices include instant download without watermark',
    },
    // FAQ Page
    faqs: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to the most common questions about how Surf Photo Lanzarote works',
      contactText: "Can't find the answer you're looking for?",
      contactLink: 'Contact us',
      questions: [
        {
          q: 'How do I find my photos?',
          a: 'Select your surf school, the day of your session, and upload a selfie. The website will show you only the photos where you appear that day.',
        },
        {
          q: 'Why do I need to upload a selfie?',
          a: 'We use facial recognition technology to identify your face and show you only your photos, without having to search through hundreds of images.',
        },
        {
          q: 'What happens to the selfie I upload?',
          a: 'The selfie is used only to perform the search for that day and is not stored or used for any other purpose.',
        },
        {
          q: 'Can I search photos from multiple days with a single selfie?',
          a: 'No. Each search is done per day. If you surfed on different days, you must upload a selfie for each session to find your photos.',
        },
        {
          q: 'What should I do if we are two or more people?',
          a: 'Each person must upload their own selfie to find their photos. You can add each person\'s photos to the cart and continue without any problem.',
        },
        {
          q: 'Why do the photos have a watermark?',
          a: 'Photos are displayed with a watermark to protect the photographer\'s work. After purchase, you can download them without a watermark.',
        },
        {
          q: 'What quality are the downloaded photos?',
          a: 'Photos are downloaded in high quality, ready for personal use, social media, and printing.',
        },
        {
          q: 'How long will my photos be available?',
          a: 'Photos remain online for a limited time. We recommend downloading them as soon as possible after purchase.',
        },
        {
          q: 'Can I buy photos from more than one day?',
          a: 'Yes. You can access different days and purchase photos from each session independently.',
        },
        {
          q: 'Are the photos public?',
          a: 'No. Photos are organized by school and day so that each client can access only their own session.',
        },
        {
          q: 'Is the payment secure?',
          a: 'Yes. All payments are processed through Stripe, a secure and reliable payment platform.',
        },
        {
          q: 'What should I do if I can\'t find myself or have a problem?',
          a: 'If you don\'t appear in the photos or have any issue, you can contact us and we will help you.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Select a Day',
      subtitle: 'Choose the day of your surf session',
      noPhotos: 'No photos available for this school yet.',
      back: 'Back',
      searchByDate: 'Search by date',
      search: 'Search',
      availableDays: 'Available days',
      selectDaySubtitle: 'Select a day to find your photos',
      loadingDays: 'Loading available days...',
      noDaysYet: 'No days available for this school yet.',
      noDaysDesc: 'Photos are organized by day. Check back soon for new sessions.',
      photos: 'photos',
      errorLoadingDays: 'Error loading available days',
      noPhotosForDate: 'No photos available for that date',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Your Surf Photos',
      subtitle: 'Photos from',
      findTitle: 'Find your photos',
      findSubtitle: 'Choose whether to upload a selfie or turn on the camera so we can detect your face.',
      clickToSelect: 'Click to select a selfie',
      orDragImage: 'or drag an image here',
      faceGuide: {
        hintStarting: 'Preparing facial guide…',
        hintNoFace: 'Place your face inside the oval',
        hintMultiple: 'Only one person in the camera, please',
        hintTooFar: 'Move a little closer',
        hintTooClose: 'Move a little farther away',
        hintMoveLeft: 'Move a little to the left',
        hintMoveRight: 'Move a little to the right',
        hintMoveUp: 'Raise your face a little',
        hintMoveDown: 'Lower your face a little',
        hintLookStraight: 'Look straight at the camera',
        turnLeft: 'Turn your head to the left and hold',
        turnRight: 'Now turn your head to the right',
        circleHead: 'Slowly circle your head',
        coverage: 'Face {{pct}}%',
        hintHoldStill: 'Perfect, hold still…',
        hintReady: 'Captured',
        cameraDenied: 'Camera permission was denied. You can upload a selfie or try again.',
        choosePrompt: 'How should we find your face?',
        uploadSelfie: 'Upload a selfie',
        uploadSelfieHint: 'Use a photo you already have',
        startCamera: 'Turn on the camera',
        startCameraHint: 'We guide you so we can detect your face',
        backToChoice: 'Choose another option',
        capture: 'Capture',
        useGallery: 'Upload from gallery',
        loadingDetector: 'Starting camera…',
        galleryMultipleFaces: 'Upload a selfie with only one person (no group photos).',
        galleryNoFace: 'No face detected in the image. Try another selfie.',
      },
      changePhoto: 'Change photo',
      analyzing: 'Analyzing...',
      searchPhotos: 'Find my photos',
      errorReadFile: 'Error reading file. Please try another image.',
      errorSelectFirst: 'Please select a photo first',
      errorEmptyFile: 'The selected file is empty. Please select another image.',
      errorAnalyze: 'Error analyzing photo. Check that the server is running.',
      yourSelfie: 'Your selfie',
      analyzingFace: 'Analyzing your face...',
      streamingMatchesSoFar: '{{count}} matches found so far — still searching…',
      slowPathHint:
        'this folder is not indexed yet, so we’re scanning photo by photo (slow first time only).',
      autoIndexHint:
        'Building the index in the background — your next search in this folder will be much faster.',
      indexClustersHint:
        'Creating the face index (clusters)… this happens once per school/day — then searches use clusters only.',
      searchDiagnostics:
        'Facial search: {{path}} · mode {{mode}} · {{seconds}}s',
      searchingIn: 'Searching for matches in',
      analysisResults: 'Analysis Results',
      matches: 'Matches',
      photosAnalyzed: 'Photos Analyzed',
      percentage: 'Percentage',
      photosFound: 'Photos Found',
      addAllMemories: 'Add all your memories',
      addAllMemoriesFor35: 'Add all your memories for {{price}}€',
      similarity: 'Similarity',
      buy: 'Buy',
      viewLarge: 'View full size',
      noMatch: 'No Match',
      moreItems: 'more',
      uploadPhoto: 'Upload Your Photo',
      uploadDescription: 'Upload a clear photo of your face to find your surf photos',
      selectFile: 'Select Photo',
      findPhotos: 'Find My Photos',
      results: 'photos found',
      addToCart: 'Add to Cart',
      noResults: 'No photos found. Try uploading a different photo.',
      savedSearchBanner: 'Showing your saved search — all matched photos from your account.',
      newSearchLink: 'Run a new search with another selfie',
      savedSearchLoading: 'Loading saved search...',
      savedSearchNotFound: 'Saved search not found. You can run a new search below.',
      partnerOffer: 'Offer',
      partnerOfferCta: 'View offer',
      partnerOffersWhileSearching: 'Partner offers while we search your photos',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Sign in',
      registerTitle: 'Create your profile',
      loginLead: 'Access your purchases and saved searches for up to 90 days.',
      continueGoogle: 'Continue with Google',
      orEmail: 'or with email',
      displayName: 'Name',
      email: 'Email',
      password: 'Password',
      passwordStrengthLabel: 'Password strength',
      strengthWeak: 'Weak',
      strengthFair: 'Fair',
      strengthGood: 'Good',
      strengthStrong: 'Strong',
      strengthHint: 'Use uppercase, numbers and symbols',
      signIn: 'Sign in',
      signUp: 'Create account',
      forgotPassword: 'Forgot your password?',
      forgotPasswordTitle: 'Reset your password',
      forgotPasswordLead: 'Enter your email and we will send you a link to choose a new password.',
      sendResetLink: 'Send reset link',
      resetLinkSent: 'If an account exists with that email, we sent a reset link.',
      resetLinkError: 'Could not send the reset link. Try again.',
      backToSignIn: 'Back to sign in',
      resetPasswordTitle: 'Choose a new password',
      resetPasswordLead: 'Enter a new password for your account.',
      newPassword: 'New password',
      savePassword: 'Save password',
      passwordResetSuccess: 'Password updated. You can sign in now.',
      passwordResetInvalid: 'This reset link expired or is invalid.',
      passwordResetError: 'Could not update your password. Try again.',
      passwordMismatch: 'Passwords do not match.',
      confirmPassword: 'Confirm password',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      createAccount: 'Create one',
      photographerCta: 'Are you a photographer?',
      photographerRegister: 'Create photographer account',
      photographerPanel: 'Photographer panel',
      verifyEmailSent: 'Check your email to verify your account.',
      backHome: 'Back to home',
      oauthLoading: 'Signing you in...',
      oauthError: 'Sign in failed. Try again.',
      oauthMissing: 'Invalid sign-in link.',
      emailVerifiedTitle: 'Email verified',
      emailVerifiedBody: 'Your profile is active. You can sign in and access your gallery.',
      goToAccount: 'Go to my account',
      emailInvalidTitle: 'Invalid link',
      emailInvalidBody: 'The verification link expired or is invalid.',
      checkEmailTitle: 'Check your email',
      checkEmailBody: 'We sent you a link to verify your account.',
      checkEmailSpam: 'If you do not see it, check spam or promotions.',
      resendVerification: 'Resend verification email',
      resendSuccess: 'If the account exists, we sent a new email.',
      resendError: 'Could not resend the email. Try again.',
      resendEmailRequired: 'Enter your email to resend.',
      emailNotConfigured:
        'We could not send the email from the server. Contact support or try Google sign-in.',
      registerCreatedNoEmailTitle: 'Account created',
      registerCreatedNoEmailBody:
        'Your account was saved, but we could not send the verification email yet. Use resend below or sign in with Google.',
      galleryTitle: 'My gallery',
      profileSubtitle: 'Your purchases and saved searches, up to 90 days after each session.',
      tabProfile: 'Profile',
      hello: 'Hello',
      signOut: 'Sign out',
      tabPurchases: 'Purchases',
      tabSearches: 'Saved searches',
      statPurchases: 'Purchases',
      statSearches: 'Searches',
      statPhotos: 'Photos',
      editProfile: 'Edit profile',
      saveProfile: 'Save changes',
      profileSaved: 'Profile updated',
      profileSaveError: 'Could not save profile',
      changePhoto: 'Change photo',
      removePhoto: 'Remove photo',
      photoHint: 'JPG or PNG, max 5 MB. Used as your profile picture.',
      photoUploadError: 'Could not upload photo',
      photoRemoved: 'Photo removed',
      photoUploaded: 'Photo updated',
      emailReadonly: 'Email cannot be changed here',
      authGoogle: 'Google account',
      authEmail: 'Email & password',
      verified: 'Verified',
      photosInOrder: 'photos',
      orderDate: 'Order date',
      downloadAvailable: 'Download available',
      loadError: 'Could not load your gallery.',
      emailVerifyRequired:
        'Verify your email to access your gallery, purchases and downloads.',
      verifyEmailLink: 'Verify email',
      noPurchases: 'You have no purchases yet.',
      noSearches: 'No saved searches yet.',
      order: 'Order',
      downloadZip: 'Download ZIP',
      expired: 'Download period ended',
      newSearch: 'Find photos from another day',
      photosFound: 'photos found',
      viewSession: 'View session',
      searchSavedAt: 'Search saved',
      loading: 'Loading...',
    },
    profilePrompt: {
      beforePayTitle: 'Create your profile',
      beforePayBody:
        'Sign in with Google or email to download your photos anytime for up to 90 days after the session date.',
      afterPayTitle: 'Save your photos to your profile',
      afterPayBody:
        'Without a profile you can only download once now. With a profile you can access them anytime for 90 days.',
      exitTitle: 'Save your results',
      exitBody: 'Create a profile so you do not need to upload your selfie again next time.',
      createProfile: 'Create profile',
      saveToProfile: 'Save to my profile',
      saveResults: 'Save results',
      continueGoogle: 'Continue with Google',
      notNow: 'Not now',
      downloadNowOnly: 'Download now only',
    },
    checkoutSuccess: {
      loading: 'Loading your downloads...',
      paymentNotFound: 'Payment not found',
      noDownloadData: 'No download data.',
      testHint: 'To test the downloads section without paying, visit:',
      backHome: 'Back to home',
      title: 'Payment completed!',
      subtitle: 'Thank you for your purchase. Here are your photos to download without watermark.',
      hello: 'Hello',
      downloads: 'Downloads',
      downloadsIntro: 'You can download each photo in high quality by clicking the button or the image.',
      downloadAll: 'Download all photos',
      downloadingAll: 'Starting download…',
      downloadThis: 'Download this photo',
      downloadingOne: 'Downloading…',
      downloadTitle: 'Download',
      total: 'Total',
      photo: 'photo',
      photos: 'photos',
      exploreMore: 'See more schools',
      missingSession: 'Missing session_id or test mode (test=1).',
      paymentIncomplete:
        "We couldn't load your payment details right now, but if you completed the payment, it went through — please don't pay again. Refresh this page in a moment, or contact us with your email and we'll send you the photos.",
      loadError: 'Could not load payment details.',
      unexpectedResponse: 'Unexpected server response.',
      connectionError: 'Connection error. Check that the backend is running.',
      downloadsOnThisPage:
        'Your photos are on this page. Use "Download all photos" or each "Download this photo" button below.',
      guestProfileBenefit:
        'You can download your photos here anytime with this link. Create a profile to save them in your gallery and access them whenever you want.',
      createProfileCta: 'Create profile',
      goToGallery: 'My gallery',
      downloadFailed: 'Download failed. Try "Download all photos" or refresh this page.',
      voucherEmail:
        'We also emailed your purchase confirmation with partner voucher PDFs.',
    },
    checkoutCancel: {
      title: 'Payment cancelled',
      message: 'No charge was made. You can return to the cart or keep browsing.',
      backHome: 'Back to home',
      viewSchools: 'View schools',
    },
    // Cart Modal
    cart: {
      title: 'Your Memories',
      item: 'item',
      items: 'items',
      empty: 'Your cart is empty',
      emptyDesc: 'Explore our photos and add your favorite memories',
      explorePhotos: 'Explore Photos',
      total: 'Total',
      checkout: 'Proceed to Checkout',
      remove: 'Remove',
      beforePaymentDownloadNotice:
        'Your photos will be available to download on this same page immediately after payment',
      emailPlaceholder: 'Your email',
      namePlaceholder: 'Your name',
      subtotal: 'Subtotal',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Prices shown exclude tax',
      discount: 'Discount',
      processing: 'Processing...',
      proceedToPayment: 'Proceed to Payment',
      continueShopping: 'Continue Shopping',
      securePayment: 'Secure Payment',
      instantDownload: 'Instant Download',
      closeCart: 'Close cart',
      removeItem: 'Remove item',
      packComplete: 'Full pack',
      photo: 'photo',
      photos: 'photos',
      digitalFormat: 'Digital Format',
      highResolution: 'High Resolution',
      errorEmailNameRequired: 'Enter your email and name to continue',
      errorInvalidEmail: 'Enter a valid email (e.g. you@email.com)',
      errorEmptyCart: 'Your cart is empty',
      errorCheckoutSession: 'Could not create payment session',
      errorNoPaymentUrl: 'Payment URL not received. Check that Stripe is configured.',
      errorPaymentFailed: 'Error processing payment',
    },
    // Sticky purchase flow pricing bar
    purchasePricingBar: {
      ariaLabel: 'Pricing',
      disclaimer:
        'Discount on 2nd pack applies when both packs are added to cart together. Prices exclude tax (IGIC).',
      blocks: [
        {
          description: 'Your best photo',
          badge: '1 photo',
        },
        {
          description: 'All your photos from a session',
          badge: 'Full pack',
        },
        {
          description: 'Extra session for you',
          badge: '2nd pack',
        },
        {
          description: 'All photos of a friend or family',
          badge: '2nd pack',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Partner Schools',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Premium surf photography marketplace in Lanzarote',
      navigation: 'Navigation',
      home: 'Home',
      schools: 'Schools',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      legal: 'Legal',
      legalNotice: 'Legal Notice',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      contact: 'Contact',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, Canary Islands',
      rights: 'All rights reserved',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      close: 'Close',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      back: 'Back',
    },
  };
